import { AxiosResponse } from "axios";

type DownloadOptions = {
  fallbackFilename?: string;
  format?: "csv" | "xlsx";
};

/**
 * Parse filename from Content-Disposition header
 * Handles various formats: filename="name.ext", filename=name.ext, filename*=UTF-8''name.ext
 */
function parseFilenameFromContentDisposition(
  contentDisposition: string | undefined
): string | null {
  if (!contentDisposition) return null;

  // Try filename*= (RFC 5987) first - handles UTF-8 encoded filenames
  const utf8Match = /filename\*=(?:UTF-8''|utf-8'')([^;\s]+)/i.exec(
    contentDisposition
  );
  if (utf8Match && utf8Match[1]) {
    return decodeURIComponent(utf8Match[1]);
  }

  // Try filename="..." (quoted)
  const quotedMatch = /filename="([^"]+)"/i.exec(contentDisposition);
  if (quotedMatch && quotedMatch[1]) {
    return quotedMatch[1];
  }

  // Try filename=... (unquoted)
  const unquotedMatch = /filename=([^;\s]+)/i.exec(contentDisposition);
  if (unquotedMatch && unquotedMatch[1]) {
    return unquotedMatch[1];
  }

  return null;
}

/**
 * Get MIME type based on format
 */
function getMimeType(format?: "csv" | "xlsx"): string {
  switch (format) {
    case "xlsx":
      return "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
    case "csv":
    default:
      return "text/csv";
  }
}

/**
 * Get file extension based on format
 */
function getFileExtension(format?: "csv" | "xlsx"): string {
  return format === "xlsx" ? ".xlsx" : ".csv";
}

/**
 * Download a file from an API response
 * Properly handles content-disposition headers and file extensions
 */
export async function downloadHelper(
  fn: () => Promise<AxiosResponse>,
  options: DownloadOptions = {}
) {
  const { fallbackFilename = "export", format = "csv" } = options;

  // Call the API function to get the data with authorization
  const response = await fn();

  // Determine MIME type
  const mimeType = getMimeType(format);

  // Create a download URL from the blob data
  const blob = new Blob([response.data || new Blob()], { type: mimeType });
  const url = window.URL.createObjectURL(blob);

  // Try to get filename from Content-Disposition header
  const contentDisposition = response.headers?.["content-disposition"];
  let filename = parseFilenameFromContentDisposition(contentDisposition);

  // If no filename from header, use fallback with proper extension
  if (!filename) {
    const extension = getFileExtension(format);
    filename = fallbackFilename.endsWith(extension)
      ? fallbackFilename
      : `${fallbackFilename}${extension}`;
  }

  // Replace spaces with underscores
  const sanitizedFilename = filename.replace(/ /g, "_");

  // Create a hidden anchor to trigger the download
  const downloadLink = document.createElement("a");
  downloadLink.href = url;
  downloadLink.setAttribute("download", sanitizedFilename);

  // Trigger download
  document.body.appendChild(downloadLink);
  downloadLink.click();
  document.body.removeChild(downloadLink);

  // Clean up the URL object
  window.URL.revokeObjectURL(url);
}
